import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import TeamCarousel from "@/components/TeamCarousel";
import { Reveal } from "@/components/Reveal";
import { TEAM, ABOUT_IMAGE } from "@/lib/content";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About us"
        title="Organisational culture is human. So are we."
        description="Advanced Risk Management Solutions Ltd exists because rules alone never ensured optimal performance. Whether that is safety, quality or productivity. We work on the attitudes, behaviours and culture that decide what really happens when nobody is watching."
        image={ABOUT_IMAGE}
        alt="Industrial safety technician inspecting infrastructure at height"
      />

      {/* PHILOSOPHY */}
      <section className="bg-white py-20 lg:py-32" data-testid="about-philosophy">
        <div className="container-arms grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow mb-4">Our philosophy</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              People don't break rules because they're careless. They break them because the culture lets them.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                Most improvement programmes treat people as the problem to be controlled. We start from a different place: people are the solution to be understood. When workers take risks, there is always a reason — pressure, habit, a process that doesn't fit the work, a leader who looked away.
              </p>
              <p>
                Our work combines behavioural science, professional acting and performance management to find those reasons, make them impossible to ignore, and replace them with habits that hold under real-world pressure.
              </p>
              <p className="font-semibold text-navy">
                Changing attitudes, influencing behaviour and transforming culture
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="bg-slate-50 py-20 lg:py-28" data-testid="about-experience">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">Experience</p>
            <h2 className="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Trusted where failure is not an option
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["21+", "years working in partnership with complex, high-hazard organisations"],
              ["3000+", "actor-led workshops designed and delivered"],
              ["12", "safety-critical sectors from rail to nuclear across 65 different countries"],
              ["£millions", "of client savings through reduced risk"],
            ].map(([v, l], i) => (
              <Reveal key={l} delay={i * 0.08}>
                <div className="rounded-3xl border border-slate-200 bg-white p-8">
                  <p className="font-display text-3xl sm:text-4xl font-extrabold text-teal">{v}</p>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed">{l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRIAD MODEL */}
      <section className="bg-white py-20 lg:py-32" data-testid="about-triad">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">Why it works</p>
            <h2 className="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">One team, three disciplines</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              ["/assets/icon-culture-assessment.png", "Behavioural Science", "Assessment and cultural analysis rooted in psychology — we evaluate what people truly believe not what they should believe."],
              ["/assets/icon-bespoke-workshops.png", "Professional Acting", "Consultant-actors recreate your workplace's defining moments, creating emotional shifts no presentation can."],
              ["/assets/icon-performance-management.png", "Performance Management", "Systems, processes and procedures that encourage the right behaviours and form the right habits"],
            ].map(([icon, title, text], i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:p-10">
                  <span className="inline-flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm"><img src={icon} alt="" loading="lazy" className="h-full w-full object-cover" /></span>
                  <h3 className="mt-5 text-xl sm:text-2xl font-bold tracking-tight text-navy">{title}</h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="about-team">
        <div className="container-arms">
          <Reveal>
            <p className="eyebrow mb-4">Our core team</p>
            <h2 className="max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Behavioural scientists, psychologists, consultant actors - one team!
            </h2>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Behind our core team sits a network of behavioural scientists, psychologists, consultant actors with huge amounts of industrial experience across 12 different sectors. Get in touch and let's have a conversation.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1}>
                <article className="group rounded-3xl border border-slate-200 bg-white p-5" data-testid={`team-member-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="overflow-hidden rounded-2xl">
                    <img src={m.image} alt={`Portrait of ${m.name}`} loading="lazy" className={`aspect-[4/5] w-full object-cover img-duotone transition-transform duration-500 group-hover:scale-105 ${m.imgPos || "object-center"}`} />
                  </div>
                  <h3 className="mt-5 px-2 text-xl font-bold tracking-tight text-navy">{m.name}</h3>
                  <p className="mt-1 px-2 font-mono text-xs uppercase tracking-[0.15em] text-teal">{m.role}</p>
                  <p className="mt-3 px-2 pb-2 text-sm text-slate-600 leading-relaxed">
                    {m.bio}
                    {m.linkedin && (
                      <>
                        {" "}
                        <a
                          href={m.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${m.name} on LinkedIn`}
                          data-testid={`linkedin-link-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="inline-flex translate-y-[2px] items-center transition-transform duration-200 hover:scale-105"
                        >
                          <img src="/assets/logo-linkedin.png" alt="LinkedIn" className="h-4 w-auto" />
                        </a>
                      </>
                    )}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
          <div className="mt-20">
            <TeamCarousel />
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 text-sm text-slate-500">
              <Link to="/contact" className="font-semibold text-teal hover:text-navy transition-colors">Get in touch <ArrowRight className="inline" size={14} /></Link>
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand title="Talk to the people behind the work" text="Every engagement starts with an honest conversation about your culture — no obligation, no jargon." />
    </div>
  );
}
